import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Divider,
  Avatar,
  useTheme,
  alpha,
  Alert,
} from '@mui/material';
import { Save, Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';
import MainLayout from '../components/Layout/MainLayout';

const Profile = () => {
  const theme = useTheme();
  const { user, setUser } = useAuth();

  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [saving, setSaving] = useState(false);

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    re_new_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await api.patch('/auth/users/me/', {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
      });
      if (setUser) setUser(response.data);
      toast.success('Profile updated');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update profile';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    if (passwords.new_password !== passwords.re_new_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwords.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/users/set_password/', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
        re_new_password: passwords.re_new_password,
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', re_new_password: '' });
    } catch (error) {
      const data = error.response?.data;
      if (data?.current_password) {
        setPasswordError(data.current_password[0]);
      } else if (data?.new_password) {
        setPasswordError(data.new_password[0]);
      } else {
        setPasswordError(data?.detail || 'Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = [user?.first_name?.[0], user?.last_name?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase() || user?.email?.[0]?.toUpperCase() || '?';

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Profile
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account information
          </Typography>
        </Box>

        {/* Profile Header */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: theme.palette.primary.main,
                fontSize: 32,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Personal Information
            </Typography>
            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileData.first_name}
                  onChange={(e) =>
                    setProfileData({ ...profileData, first_name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileData.last_name}
                  onChange={(e) =>
                    setProfileData({ ...profileData, last_name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={profileData.email}
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveProfile}
                  disabled={saving}
                  size="large"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Lock sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h5" fontWeight={600}>
                Change Password
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />

            {passwordError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {passwordError}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={passwords.current_password}
                  onChange={(e) =>
                    setPasswords({ ...passwords, current_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={passwords.new_password}
                  onChange={(e) =>
                    setPasswords({ ...passwords, new_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={passwords.re_new_password}
                  onChange={(e) =>
                    setPasswords({ ...passwords, re_new_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Lock />}
                  onClick={handleChangePassword}
                  disabled={
                    changingPassword ||
                    !passwords.current_password ||
                    !passwords.new_password ||
                    !passwords.re_new_password
                  }
                  size="large"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default Profile;
